/**
 * Save-integrity orchestration for the chat Settings tab.
 *
 * The Settings submission is modelled as a list of explicit "requested
 * operations" (chat update, catalog assignment, price visibility, MOV). Each
 * one owns the form fields it persists and a `run` that RESOLVES on success or
 * REJECTS on any failure (GraphQL, business-union, network, or client-side
 * validation).
 *
 * The success boundary spans *every* requested operation:
 *  - if all succeed → `resetAll()` makes the whole form the new clean baseline
 *  - if some fail  → only the succeeded operations `commit()` (mark their own
 *    fields clean); the failed operations' fields stay dirty, so Save stays
 *    enabled and the failure is retryable. The form is never fully reset and no
 *    overall success is announced.
 *
 * This is intentionally framework-agnostic: `run`, `reconcile`, `commit` and
 * `resetAll` are the only seams, which keeps it exhaustively unit-testable
 * without React or a form library.
 */

export interface RequestedSaveOperation {
  /** Persists the operation; resolves on success, rejects on any failure. */
  run: () => Promise<void>;
  /** Optional refetch to reconcile authoritative server state after success. */
  reconcile?: () => Promise<unknown>;
  /** Marks this operation's fields clean (only on a partial-failure save). */
  commit: () => void;
}

export interface SaveOperationsOutcome {
  requested: number;
  succeeded: number;
  failed: number;
  /** True only when every requested operation succeeded (whole-form reset). */
  allSucceeded: boolean;
}

export const runRequestedSaveOperations = async (
  operations: RequestedSaveOperation[],
  resetAll: () => void
): Promise<SaveOperationsOutcome> => {
  if (operations.length === 0) {
    return { requested: 0, succeeded: 0, failed: 0, allSucceeded: false };
  }

  // Firing order is deterministic (map iterates in order); operations run
  // independently so a valid change still commits even if another fails.
  const results = await Promise.allSettled(
    operations.map((operation) => operation.run())
  );

  const succeeded = operations.filter(
    (_operation, index) => results[index].status === 'fulfilled'
  );
  const failed = operations.filter(
    (_operation, index) => results[index].status === 'rejected'
  );

  // Reconcile only what actually succeeded. Reconcile failures are
  // NON-CRITICAL: the mutations already succeeded, so a rejected refetch (e.g.
  // `refetchPricesVisibility()`) must not escape before the commit/reset below.
  // With a bare `Promise.all` such a rejection propagated out of this function
  // BEFORE `resetAll()`/`commit()` ran — leaving successful writes dirty and
  // surfacing an unhandled rejection through react-hook-form's `handleSubmit`,
  // so the next Save re-fired already-succeeded mutations. `allSettled` lets us
  // proceed to commit/reset regardless of a reconcile outcome.
  await Promise.allSettled(
    succeeded
      .map((operation) => operation.reconcile?.())
      .filter((result): result is Promise<unknown> => Boolean(result))
  );

  if (failed.length === 0) {
    resetAll();
  } else {
    succeeded.forEach((operation) => operation.commit());
  }

  return {
    requested: operations.length,
    succeeded: succeeded.length,
    failed: failed.length,
    allSucceeded: failed.length === 0,
  };
};
